import { Button } from 'components/atoms';
import { Form } from 'components/organisms';
import { useParamId } from 'hooks/useParamId';
import middleware from 'middleware';
import { useTranslation } from 'react-i18next';
import { PiX } from 'react-icons/pi';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  useGetAgreementTemplate,
  usePutAgreementTemplate,
} from 'services/templates/agreement-template';
import { IContractTemplateForm } from 'services/templates/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { contractTemplateUtil } from 'utils';
import { onErrorShowAllErrorsToast } from 'utils/service/errorHandler';

const EditAgreementTemplate = () => {
  const id = useParamId('/templates-extended');
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isParagraphKind = searchParams.get('kind') === 'paragraph';

  const agreementTemplate = useGetAgreementTemplate(id);
  const updateAgreementTemplate = usePutAgreementTemplate(id);

  const goBackToAgreement = () =>
    navigate(
      isParagraphKind
        ? `/templates-extended/agreement/${id}/form?kind=paragraph`
        : `/templates-extended/agreement/${id}/form`
    );

  const name = agreementTemplate.data
    ? agreementTemplate.data?.title
    : undefined;

  const onUpdateAgreementTemplate = async (values: IContractTemplateForm) => {
    if (values.sideMenu?.detail) {
      return await updateAgreementTemplate.mutateAsync(
        middleware.template.agreement.formToQuery(
          values.sideMenu?.detail.content
        ),
        {
          onSuccess: goBackToAgreement,
          onError: error => onErrorShowAllErrorsToast(error),
        }
      );
    }
  };

  const getInitialValues = (): IContractTemplateForm => {
    if (!agreementTemplate.data) {
      return isParagraphKind
        ? contractTemplateUtil.agreement.initialParagraphValues()
        : contractTemplateUtil.agreement.initialValues();
    }

    return isParagraphKind
      ? middleware.template.agreement.queryToParagraphForm(agreementTemplate.data)
      : middleware.template.agreement.queryToForm(agreementTemplate.data);
  };

  return (
    <Form.Contract
      tittle={`${t('button.edit')} ${
        name ??
        (isParagraphKind
          ? t('contract-template.paragraphAgreement.title')
          : t('contract-template.agreement.title'))
      }`}
      customBackItem={
        <Button type='button' onlyIcon onClick={goBackToAgreement}>
          <Button.Description description={t('button.cancel')} />

          <Button.Icon icon={<PiX />} />
        </Button>
      }
      topbarContent={
        <>
          <Button type='submit' variant={BUTTON_VARIANT.submit}>
            <Button.Text text={t(`button.save`)} />
          </Button>
        </>
      }
      initialStatus={contractTemplateUtil.agreement.initialStatus}
      initialValues={getInitialValues()}
      onSubmit={onUpdateAgreementTemplate}
    />
  );
};

export default EditAgreementTemplate;
